import { useQuery } from '@tanstack/react-query'
import { gql } from 'graphql-request'
import { gqlClient } from '@/lib/graphql'

export interface ChapterRef {
  id: string
  position: number
  displayTitle: string
}

export interface BookReader {
  id: string
  title: string
  targetLanguage: string
  sourceLanguage: string
  githubRepoUrl: string | null
  chapters: ChapterRef[]
}

export interface ReaderSegment {
  id: string
  externalId: string
  blockType: string
  sourceText: string | null
  targetText: string | null
  sentenceMap: string | null
  translated: boolean
  status: string
  audioStatus: string
  audioUrl: string | null
  // Outdated narration the reader asked to refresh before it next plays; the
  // player treats it like missing audio and re-synthesizes first.
  regenerateBeforePlay: boolean
}

const BOOK = gql`
  query BookReader($slug: String!) {
    book(slug: $slug) {
      id
      title
      sourceLanguage
      targetLanguage
      githubRepoUrl
      chapters {
        id
        position
        displayTitle
      }
    }
  }
`

const CHAPTER = gql`
  query ChapterReader($id: ID!) {
    chapter(id: $id) {
      segments {
        id
        externalId
        blockType
        sourceText
        targetText
        sentenceMap
        translated
        status
        audioStatus
        audioUrl
        regenerateBeforePlay
      }
    }
  }
`

export function useBookReader(slug: string) {
  return useQuery({
    queryKey: ['book-reader', slug],
    queryFn: async () => (await gqlClient.request<{ book: BookReader }>(BOOK, { slug })).book,
  })
}

export function useChapter(id: string | null, poll = false, awaitingAudio = false) {
  return useQuery({
    queryKey: ['chapter', id],
    enabled: !!id,
    queryFn: async () =>
      (await gqlClient.request<{ chapter: { segments: ReaderSegment[] } }>(CHAPTER, { id })).chapter.segments,
    refetchInterval: (query) => {
      const data = query.state.data ?? []
      // Poll faster while any narration is being synthesized (or we just asked
      // for one and the job hasn't flipped it to pending yet).
      if (awaitingAudio || data.some((s) => s.audioStatus === 'audio_pending')) return 2000
      // While a translation job is running, poll until every block is translated.
      return poll && data.some((s) => !s.translated) ? 2500 : false
    },
  })
}
